"use client"

import { useEffect, useRef, useState } from "react"
import { useCountUp } from "@/hooks/use-count-up"

interface AnimatedCounterProps {
  end: number
  suffix?: string
  prefix?: string
  duration?: number
  decimals?: number
  className?: string
  locale?: string
}

export function AnimatedCounter({
  end,
  suffix = "",
  prefix = "",
  duration = 2000,
  decimals = 0,
  className = "",
  locale = "en",
}: AnimatedCounterProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true)
          setHasAnimated(true)
        }
      },
      { threshold: 0.3 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [hasAnimated])

  const count = useCountUp({
    end,
    duration,
    decimals,
    enabled: isVisible,
  })

  // Format number based on locale
  const formatNumber = (num: number) => {
    const formatted = decimals > 0 ? num.toFixed(decimals) : Math.round(num).toString()
    if (locale === 'fa') {
      const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"]
      return formatted.replace(/\d/g, (w) => persianDigits[parseInt(w)])
    }
    return formatted
  }

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatNumber(count)}
      {suffix}
    </span>
  )
}
